import { Injectable, InjectionToken, inject } from '@angular/core'
import { BehaviorSubject, Subject, Observable } from 'rxjs'
import {
  FirebaseStorage,
  deleteObject,
  ref,
  uploadBytes,
} from 'firebase/storage'
import { environment } from '../../environments/environment'
import {
  getRecordingMimeType,
  RecordingExtension,
} from './meeting-recording/mime'

/** 録音チャンク専用 Firebase Storage インスタンスのトークン */
export const RECORDING_CHUNKS_STORAGE = new InjectionToken<FirebaseStorage>(
  'RECORDING_CHUNKS_STORAGE',
)

/** 録音の状態 */
export type RecordingState = 'idle' | 'recording' | 'finalizing' | 'done'

/** チャンク分割間隔（ミリ秒） */
const CHUNK_TIMESLICE_MS = 10_000

/** アップロード完了待ちの最大時間（ミリ秒） */
const UPLOAD_WAIT_MAX_MS = 60_000

/** アップロード完了チェック間隔（ミリ秒） */
const UPLOAD_WAIT_CHECK_INTERVAL_MS = 500

/**
 * 商談録音サービス。MediaRecorder によるチャンク録音、GCS アップロード、状態管理、
 * 経過時間計測、モバイル向けの Wake Lock 取得を行う。
 */
@Injectable({
  providedIn: 'root',
})
export class MeetingRecordingService {
  private storage = inject(RECORDING_CHUNKS_STORAGE)
  private mediaRecorder: MediaRecorder | null = null
  private timerId: ReturnType<typeof setInterval> | null = null
  private pendingCheckInterval: ReturnType<typeof setInterval> | null = null
  private pendingTimeoutId: ReturnType<typeof setTimeout> | null = null
  private wakeLock: WakeLockSentinel | null = null
  private sessionId = ''
  private orgId = ''
  private chunkExtension: RecordingExtension | null = null
  private chunkIndex = 0
  private pendingUploads = 0
  private chunkPaths: string[] = []

  private failedChunkIndices: number[] = []
  private didUploadTimeout = false

  private readonly stateSubject = new BehaviorSubject<RecordingState>('idle')
  private readonly chunksSubject = new Subject<Blob>()
  private readonly elapsedSecondsSubject = new BehaviorSubject<number>(0)
  private readonly uploadProgressSubject = new BehaviorSubject<{
    uploaded: number
    total: number
  }>({
    uploaded: 0,
    total: 0,
  })

  /** 現在の録音状態 */
  readonly state$: Observable<RecordingState> = this.stateSubject.asObservable()

  /** 生成されたチャンク Blob を発行する */
  readonly chunks$: Observable<Blob> = this.chunksSubject.asObservable()

  /** 録音経過秒数 */
  readonly elapsedSeconds$: Observable<number> =
    this.elapsedSecondsSubject.asObservable()

  /** チャンクアップロード進捗 */
  readonly uploadProgress$: Observable<{ uploaded: number; total: number }> =
    this.uploadProgressSubject.asObservable()

  /** アップロード済みチャンク総数 */
  get totalChunks(): number {
    return this.uploadProgressSubject.value.total
  }

  /** アップロード失敗があったか */
  get hasUploadErrors(): boolean {
    return this.failedChunkIndices.length > 0 || this.didUploadTimeout
  }

  /** チャンクのアップロード先バケット名 */
  get chunkBucketName(): string {
    return environment.recordingChunksBucket
  }

  /**
   * 現在使用中の MIME タイプ。録音が開始されていない状態で呼び出すとエラーを投げる。
   * finalizeRecording で録音完了後に使うことを想定しており、録音未開始は不正状態。
   */
  get currentMimeType(): string {
    if (!this.mediaRecorder) {
      throw new Error(
        'currentMimeType is only available after startRecording has been called.',
      )
    }
    return this.mediaRecorder.mimeType
  }

  /**
   * サービスの状態を初期化する。画面遷移時に前回の状態が残らないよう呼び出す。
   */
  reset(): void {
    this.clearPendingTimers()
    this.releaseWakeLock()
    this.stateSubject.next('idle')
    this.elapsedSecondsSubject.next(0)
    this.uploadProgressSubject.next({ uploaded: 0, total: 0 })
    this.failedChunkIndices = []
    this.didUploadTimeout = false
    this.chunkPaths = []
    this.chunkExtension = null
  }

  /**
   * 録音を開始する。MediaRecorder を作成し、10 秒間隔のチャンク生成・GCS アップロードと
   * 経過時間計測、Wake Lock 取得を行う。
   */
  startRecording(stream: MediaStream, sessionId: string, orgId: string): void {
    const { mimeType, extension } = getRecordingMimeType()

    this.sessionId = sessionId
    this.orgId = orgId
    this.chunkExtension = extension
    this.chunkIndex = 0
    this.pendingUploads = 0
    this.chunkPaths = []
    this.failedChunkIndices = []
    this.didUploadTimeout = false
    this.uploadProgressSubject.next({ uploaded: 0, total: 0 })

    this.mediaRecorder = new MediaRecorder(stream, { mimeType })

    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) {
        this.chunksSubject.next(event.data)
        this.uploadChunk(event.data, this.chunkIndex++)
      }
    }

    this.mediaRecorder.onstop = () => {
      this.waitForPendingUploads()
    }

    this.mediaRecorder.start(CHUNK_TIMESLICE_MS)
    this.stateSubject.next('recording')
    this.startTimer()
    this.acquireWakeLock()
  }

  /**
   * 録音を停止する。MediaRecorder を停止し、経過時間計測と Wake Lock を終了する。
   */
  stopRecording(): void {
    this.stopTimer()
    this.releaseWakeLock()
    this.stateSubject.next('finalizing')

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop()
    } else {
      this.stateSubject.next('done')
    }
  }

  /**
   * 現セッションでアップロード対象になったチャンクを削除する。
   * 個別の削除が失敗しても処理を継続し、失敗分は console.warn に出力する。
   */
  async cleanupUploadedChunks(): Promise<void> {
    const chunkPaths = [...this.chunkPaths]
    const results = await Promise.allSettled(
      chunkPaths.map((path) => this.deleteChunk(path)),
    )
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.warn(
          `Failed to cleanup recording chunk ${chunkPaths[index]}:`,
          result.reason,
        )
      }
    })
    this.chunkPaths = []
  }

  /** 単一チャンクを削除する（テストの差し替えポイント） */
  private deleteChunk(path: string): Promise<void> {
    return deleteObject(ref(this.storage, path))
  }

  /**
   * visibility が visible に復帰したときに呼ぶ。録音中なら Wake Lock を再取得する。
   * Screen Wake Lock API は hidden 遷移時に OS が自動解放するため、復帰時の再取得が必要。
   */
  onVisibilityRestored(): void {
    if (this.stateSubject.value === 'recording') {
      this.acquireWakeLock()
    }
  }

  /**
   * チャンクを GCS にアップロードする。拡張子は録音開始時に確定したものを使う。
   */
  private uploadChunk(blob: Blob, index: number): void {
    if (this.chunkExtension == null) {
      throw new Error(
        'chunkExtension is not set. startRecording must be called first.',
      )
    }
    const paddedIndex = String(index).padStart(3, '0')
    const path = `${this.orgId}/recording_chunks/${this.sessionId}/chunk_${paddedIndex}.${this.chunkExtension}`
    const storageRef = ref(this.storage, path)
    this.chunkPaths.push(path)

    this.pendingUploads++
    const { uploaded, total } = this.uploadProgressSubject.value
    this.uploadProgressSubject.next({ uploaded, total: total + 1 })

    uploadBytes(storageRef, blob, { contentType: blob.type })
      .then(() => {
        const { uploaded: u, total: t } = this.uploadProgressSubject.value
        this.uploadProgressSubject.next({ uploaded: u + 1, total: t })
      })
      .catch((error) => {
        console.error(`Failed to upload chunk ${index}:`, error)
        this.failedChunkIndices.push(index)
      })
      .finally(() => {
        this.pendingUploads--
      })
  }

  /**
   * 未完了のアップロードを待ってから done に遷移する。最大 60 秒でタイムアウト。
   */
  private waitForPendingUploads(): void {
    if (this.pendingUploads <= 0) {
      this.stateSubject.next('done')
      return
    }

    this.clearPendingTimers()

    this.pendingCheckInterval = setInterval(() => {
      if (this.pendingUploads <= 0) {
        this.clearPendingTimers()
        this.stateSubject.next('done')
      }
    }, UPLOAD_WAIT_CHECK_INTERVAL_MS)

    this.pendingTimeoutId = setTimeout(() => {
      this.clearPendingTimers()
      if (this.stateSubject.value !== 'done') {
        console.error('Upload wait timeout exceeded')
        this.didUploadTimeout = true
        this.stateSubject.next('done')
      }
    }, UPLOAD_WAIT_MAX_MS)
  }

  /** アップロード完了待ちタイマーを全て破棄する */
  private clearPendingTimers(): void {
    if (this.pendingCheckInterval != null) {
      clearInterval(this.pendingCheckInterval)
      this.pendingCheckInterval = null
    }
    if (this.pendingTimeoutId != null) {
      clearTimeout(this.pendingTimeoutId)
      this.pendingTimeoutId = null
    }
  }

  /** 経過秒数タイマーを開始する */
  private startTimer(): void {
    this.elapsedSecondsSubject.next(0)
    this.timerId = setInterval(() => {
      this.elapsedSecondsSubject.next(this.elapsedSecondsSubject.value + 1)
    }, 1000)
  }

  /** 経過秒数タイマーを停止する */
  private stopTimer(): void {
    if (this.timerId != null) {
      clearInterval(this.timerId)
      this.timerId = null
    }
  }

  /** モバイル向けに Wake Lock（画面消灯防止）を取得する */
  private acquireWakeLock(): void {
    if (!navigator.wakeLock) {
      return
    }
    navigator.wakeLock
      .request('screen')
      .then((lock) => {
        this.wakeLock = lock
      })
      .catch((err: unknown) => {
        console.warn('Wake Lock acquisition failed:', err)
      })
  }

  /** 取得した Wake Lock を解放する */
  private releaseWakeLock(): void {
    if (this.wakeLock) {
      this.wakeLock.release().catch((err: unknown) => {
        console.warn('Wake Lock release failed:', err)
      })
      this.wakeLock = null
    }
  }
}
